import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import Container from '../components/common/Container';
import {
  CalendarIcon,
  ClockIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  BookOpenIcon,
} from '@heroicons/react/24/outline';
import { blogPosts } from './Blog';

interface BlogPostContent {
  slug: string;
  title: string;
  category: string;
  readTime: string;
  date: string;
  gradient: string;
  citation: string;
  sections: {
    heading?: string;
    content: string[];
  }[];
  keyTakeaways: string[];
  implications: string;
}

const blogPostContent: Record<string, BlogPostContent> = {
  'spacing-effect-learning-science': {
    slug: 'spacing-effect-learning-science',
    title: 'The Spacing Effect: Why Distributed Practice Transforms Learning',
    category: 'Learning Science',
    readTime: '6 min read',
    date: 'November 2025',
    gradient: 'from-[var(--color-electric-blue)] to-[var(--color-bright-cyan)]',
    citation: 'Yuan X. Evidence of the Spacing Effect and Influences on Perceptions of Learning and Science Curricula. Cureus. 2022 Jan 13;14(1):e21201. doi: 10.7759/cureus.21201. PMID: 35047318; PMCID: PMC8759977.',
    sections: [
      {
        content: [
          'The spacing effect is one of the most robust findings in cognitive psychology: information learned over spaced intervals is retained far better than information crammed in a single session. Yet traditional education often ignores this principle, favouring intensive study blocks over distributed practice.',
          'Recent research by Yuan (2022) provides compelling evidence for how the spacing effect influences student perceptions of learning, particularly in science curricula. The study reveals not just improved retention, but fundamental changes in how students perceive their own learning effectiveness.',
        ],
      },
      {
        heading: 'What is the Spacing Effect?',
        content: [
          'The spacing effect refers to the phenomenon where learning is more effective when study sessions are spread out over time rather than concentrated in a single session. This principle, first identified by Hermann Ebbinghaus in 1885, has been replicated thousands of times across different contexts, age groups, and subject matters.',
          'When students space their learning, they engage in what psychologists call "retrieval practice" — the act of recalling information strengthens memory more than simply re-reading material. Each spaced session forces the brain to reconstruct knowledge, building stronger neural pathways.',
        ],
      },
      {
        heading: 'The Research Evidence',
        content: [
          'Yuan\'s 2022 study examined how spacing affects both objective learning outcomes and students\' subjective perceptions of their learning in science courses. The findings were striking: students who used spaced practice not only performed better on assessments but also developed more accurate metacognitive awareness of their own knowledge.',
          'Importantly, the research showed that massed practice (cramming) often leads to an illusion of competence — students feel they know the material better than they actually do. Spaced practice, while sometimes feeling more difficult in the moment, produces more durable and accurate learning.',
        ],
      },
      {
        heading: 'Implications for Adaptive Learning',
        content: [
          'Modern adaptive learning platforms like Lumen8 are uniquely positioned to implement spacing effect principles at scale. By tracking individual student mastery and automatically scheduling review sessions at optimal intervals, these systems can ensure that every learner benefits from distributed practice.',
          'The key is personalisation: the optimal spacing interval varies based on the difficulty of the material, the student\'s current mastery level, and individual learning characteristics. AI-powered systems can analyse these factors in real-time, delivering precisely timed reviews that maximise retention while minimising study time.',
        ],
      },
    ],
    keyTakeaways: [
      'Spaced practice produces significantly better long-term retention than massed practice (cramming)',
      'Students often underestimate the effectiveness of spacing because it feels more effortful',
      'The spacing effect applies across all subjects and age groups',
      'Optimal spacing intervals are personalised — adaptive systems can optimise this automatically',
      'Spacing combined with retrieval practice creates the strongest memory formation',
    ],
    implications: 'For educators and parents, the spacing effect research suggests a fundamental rethink of how study time is structured. Rather than intensive revision sessions before exams, regular, shorter practice sessions throughout the term produce better outcomes. Adaptive learning platforms can automate this process, ensuring students review material at scientifically optimal intervals.',
  },

  'ai-adaptive-learning-platforms': {
    slug: 'ai-adaptive-learning-platforms',
    title: 'AI-Enabled Adaptive Learning: A Comprehensive Review',
    category: 'EdTech Research',
    readTime: '8 min read',
    date: 'November 2025',
    gradient: 'from-[var(--color-violet)] to-[var(--color-electric-blue)]',
    citation: 'Le Ying Tan, Shiyu Hu, Darren J. Yeo, Kang Hao Cheong. Artificial intelligence-enabled adaptive learning platforms: A review. Computers and Education: Artificial Intelligence, Volume 9, 2025, 100429, ISSN 2666-920X, https://doi.org/10.1016/j.caeai.2025.100429.',
    sections: [
      {
        content: [
          'Artificial intelligence is fundamentally transforming how educational content is delivered and personalised. A comprehensive 2025 review by Tan et al. examines the current landscape of AI-enabled adaptive learning platforms, revealing both remarkable progress and important considerations for future development.',
          'The review analyses dozens of platforms and research studies, identifying key patterns in how AI is being used to personalise education at scale.',
        ],
      },
      {
        heading: 'The Evolution of Adaptive Learning',
        content: [
          'Adaptive learning has evolved dramatically from early rule-based systems to sophisticated AI-powered platforms capable of modelling individual learner characteristics in real-time. The latest generation of platforms uses machine learning algorithms to analyse patterns in student behaviour, predict areas of difficulty, and dynamically adjust content presentation.',
          'Unlike earlier systems that relied on pre-programmed decision trees, modern AI-enabled platforms can discover unexpected patterns in learning data, identifying intervention opportunities that human instructors might miss.',
        ],
      },
      {
        heading: 'Key Capabilities of AI-Enabled Platforms',
        content: [
          'The review identifies several core capabilities that distinguish effective AI-enabled adaptive learning: intelligent content sequencing that adjusts to learner performance; predictive analytics that identify at-risk students before they fall behind; natural language processing for understanding student responses and providing meaningful feedback; and learning analytics dashboards that give educators actionable insights.',
          'Particularly promising is the integration of multiple AI techniques. For example, combining knowledge tracing (tracking what a student knows) with affective computing (understanding emotional states) allows platforms to adapt not just to cognitive needs but to motivational and emotional factors as well.',
        ],
      },
      {
        heading: 'Effectiveness Evidence',
        content: [
          'The research literature consistently shows positive effects for AI-enabled adaptive learning, though effect sizes vary considerably depending on implementation quality. Studies show the largest benefits when AI adaptation is combined with quality content and ongoing teacher involvement.',
          'Importantly, AI-enabled platforms appear to particularly benefit students who might otherwise struggle in traditional settings. By providing immediate, personalised support, these systems can catch learning gaps before they compound into larger problems.',
        ],
      },
      {
        heading: 'Challenges and Future Directions',
        content: [
          'The review also identifies important challenges: ensuring equity of access, protecting student privacy, maintaining transparency in AI decision-making, and avoiding over-reliance on technology at the expense of human connection.',
          'Future directions include more sophisticated modelling of learning processes, better integration of multi-modal data (including video and speech), and development of AI systems that can explain their recommendations in ways teachers and students can understand.',
        ],
      },
    ],
    keyTakeaways: [
      'AI-enabled adaptive learning represents a significant advancement over rule-based systems',
      'Most effective when AI capabilities are combined with quality content and teacher involvement',
      'Particularly benefits students who struggle in traditional learning environments',
      'Privacy, transparency, and equity are critical considerations for implementation',
      'Future platforms will integrate multiple AI techniques for more holistic personalisation',
    ],
    implications: 'For schools considering adaptive learning platforms, the research suggests looking beyond surface features to understand how AI is actually being used. The most effective platforms combine sophisticated AI with transparency about how decisions are made, give teachers meaningful oversight, and maintain focus on learning outcomes rather than engagement metrics alone.',
  },

  'personalised-learning-student-performance': {
    slug: 'personalised-learning-student-performance',
    title: 'Personalised Learning: Improving Student Performance and Satisfaction',
    category: 'Research Insights',
    readTime: '7 min read',
    date: 'October 2025',
    gradient: 'from-[var(--color-emerald)] to-[var(--color-bright-cyan)]',
    citation: 'Contrino, M.F., Reyes-Millán, M., Vázquez-Villegas, P. et al. Using an adaptive learning tool to improve student performance and satisfaction in online and face-to-face education for a more personalized approach. Smart Learn. Environ. 11, 6 (2024). https://doi.org/10.1186/s40561-024-00292-y',
    sections: [
      {
        content: [
          'Can adaptive learning tools genuinely improve both academic performance and student satisfaction? A 2024 study by Contrino et al. provides compelling evidence that the answer is yes — but the implementation approach matters significantly.',
          'The research examined the use of adaptive learning tools in both online and face-to-face educational settings, revealing important insights about how personalisation affects learning outcomes across different contexts.',
        ],
      },
      {
        heading: 'Study Design and Findings',
        content: [
          'The study compared student outcomes across multiple cohorts using adaptive learning tools versus traditional approaches. Results showed statistically significant improvements in both academic performance and student satisfaction scores when adaptive tools were properly implemented.',
          'Particularly noteworthy was the finding that benefits were consistent across both online and face-to-face learning environments. This suggests that adaptive learning\'s value comes from the personalisation itself, rather than being specific to any particular delivery mode.',
        ],
      },
      {
        heading: 'The Satisfaction Connection',
        content: [
          'Beyond test scores, the study examined student satisfaction — an important but often overlooked outcome. Students using adaptive tools reported higher engagement, reduced frustration, and greater confidence in their learning.',
          'The researchers suggest this satisfaction boost comes from multiple factors: students appreciate receiving content at the right difficulty level, getting immediate feedback helps maintain momentum, and the sense of progress from mastery-based advancement is intrinsically motivating.',
        ],
      },
      {
        heading: 'What Makes Personalisation Effective',
        content: [
          'The study identified several factors that distinguished successful adaptive learning implementations. First, the adaptive system needed to provide meaningful variation — not just adjusting difficulty but actually presenting content in different ways based on learning style and prior knowledge.',
          'Second, feedback quality mattered as much as feedback timing. Students benefited most when they received not just correctness information but explanatory feedback that helped them understand why answers were right or wrong.',
          'Third, instructor involvement remained crucial. The most successful implementations used adaptive tools to augment rather than replace teacher interaction, freeing instructors to provide targeted support where it was most needed.',
        ],
      },
    ],
    keyTakeaways: [
      'Adaptive learning tools improve both performance and satisfaction when properly implemented',
      'Benefits are consistent across online and face-to-face environments',
      'Meaningful variation in content presentation is more important than simple difficulty adjustment',
      'Quality explanatory feedback amplifies learning gains',
      'Teacher involvement remains essential for maximum effectiveness',
    ],
    implications: 'For parents and educators, this research reinforces that adaptive learning is not about replacing human instruction but augmenting it. The most effective approach combines AI-powered personalisation with engaged teachers who use the insights from adaptive systems to provide targeted support. Student satisfaction improvements suggest that good adaptive learning doesn\'t just produce better test scores — it creates a more positive relationship with learning itself.',
  },

  'mastery-learning-practical-review': {
    slug: 'mastery-learning-practical-review',
    title: 'Mastery Learning: A Practical Review for Modern Educators',
    category: 'Teaching Strategies',
    readTime: '5 min read',
    date: 'October 2025',
    gradient: 'from-[var(--color-amber)] to-[var(--color-rose)]',
    citation: 'Winget M, Persky AM. A Practical Review of Mastery Learning. Am J Pharm Educ. 2022 Dec;86(10):ajpe8906. doi: 10.5688/ajpe8906. Epub 2022 Jan 13. PMID: 35027359; PMCID: PMC10159400.',
    sections: [
      {
        content: [
          'Mastery learning — the idea that students should demonstrate competency before advancing — has been discussed in education for over a century. But what does the evidence actually say, and how can modern educators implement it effectively? A comprehensive 2022 review by Winget and Persky provides practical answers.',
          'Drawing from pharmacy education research but with broad applicability, this review synthesises decades of mastery learning studies into actionable guidance.',
        ],
      },
      {
        heading: 'The Mastery Learning Framework',
        content: [
          'At its core, mastery learning rejects the notion that some students are simply "fast learners" and others "slow learners." Instead, it proposes that given appropriate time and support, nearly all students can achieve high levels of learning. The difference between students is not ability but the time and support needed.',
          'This philosophy has profound implications for curriculum design. Rather than covering content at a fixed pace and accepting variable achievement, mastery learning fixes the achievement level and accepts variable time to mastery.',
        ],
      },
      {
        heading: 'Evidence for Effectiveness',
        content: [
          'The review examines effect sizes across numerous studies. Meta-analyses consistently show positive effects, with mastery learning producing better outcomes than traditional instruction. Particularly strong effects are seen for procedural skills and in contexts where prerequisite knowledge is important.',
          'However, the review notes important nuances: mastery learning requires more upfront development time, and implementation quality varies widely. Not all "mastery" programs are equally effective.',
        ],
      },
      {
        heading: 'Practical Implementation Guidance',
        content: [
          'For educators, the review offers concrete recommendations. First, define clear mastery criteria — vague standards make consistent assessment impossible. Second, provide immediate feedback so students can correct misconceptions quickly. Third, offer varied learning resources so students who don\'t achieve mastery initially have alternative paths.',
          'The review also addresses common concerns: managing students who progress at different rates, maintaining motivation for students who need multiple attempts, and balancing mastery requirements with curriculum coverage pressures.',
        ],
      },
      {
        heading: 'Technology-Enabled Mastery',
        content: [
          'Modern adaptive learning platforms are ideally suited for implementing mastery learning at scale. They can track mastery across numerous learning objectives, automatically sequence content based on demonstrated competency, and provide the varied practice and feedback that mastery learning requires.',
          'The combination of mastery learning principles with AI-powered adaptation addresses one of mastery learning\'s historical challenges: the difficulty of managing individualised pacing in traditional classroom settings.',
        ],
      },
    ],
    keyTakeaways: [
      'Mastery learning is based on the principle that nearly all students can achieve high learning levels given appropriate time and support',
      'Meta-analyses consistently show positive effects compared to traditional instruction',
      'Clear mastery criteria and immediate feedback are essential implementation elements',
      'Technology-enabled adaptive learning makes mastery approaches more practical at scale',
      'Variable pacing is a feature, not a bug — it ensures genuine competency',
    ],
    implications: 'Mastery learning aligns closely with how Lumen8 approaches curriculum progression. Rather than advancing students based on seat time or calendar, mastery-based systems ensure genuine understanding before moving forward. This prevents the accumulation of knowledge gaps that often compound into larger struggles later. For students, this means every step forward rests on solid foundations.',
  },

  'zone-proximal-development-scaffolding': {
    slug: 'zone-proximal-development-scaffolding',
    title: 'Zone of Proximal Development: The Science of Scaffolded Learning',
    category: 'Learning Theory',
    readTime: '6 min read',
    date: 'September 2025',
    gradient: 'from-[var(--color-rose)] to-[var(--color-violet)]',
    citation: 'Raslan, G. (2024). The Impact of the Zone of Proximal Development Concept (Scaffolding) on the Students Problem Solving Skills and Learning Outcomes. In: Al Marri, K., Mir, F.A., David, S.A., Al-Emran, M. (eds) BUiD Doctoral Research Conference 2023. Lecture Notes in Civil Engineering, vol 473. Springer, Cham. https://doi.org/10.1007/978-3-031-56121-4_6',
    sections: [
      {
        content: [
          'Lev Vygotsky\'s Zone of Proximal Development (ZPD) remains one of the most influential concepts in educational psychology. Recent research by Raslan (2024) examines how this century-old theory applies to modern learning environments, particularly for developing problem-solving skills.',
          'The ZPD describes the space between what a learner can do independently and what they can achieve with guidance — the sweet spot where learning happens most effectively.',
        ],
      },
      {
        heading: 'Understanding the Zone of Proximal Development',
        content: [
          'Vygotsky proposed that at any given moment, each learner has three zones of capability: what they can do independently, what they cannot do even with help, and the crucial middle zone — what they can accomplish with appropriate support. This middle zone is where instruction should be targeted.',
          'The ZPD is not static. As students develop competence, their ZPD shifts. Effective instruction therefore requires continuous assessment of where each student\'s ZPD lies and adjustment of support accordingly.',
        ],
      },
      {
        heading: 'Scaffolding as a Teaching Strategy',
        content: [
          'Scaffolding is the practical application of ZPD theory. Like physical scaffolding on a building, educational scaffolding provides temporary support that is gradually removed as the learner develops independent capability.',
          'Effective scaffolding is contingent — it responds to the specific difficulties a learner encounters rather than providing generic assistance. It\'s also faded — reduced progressively as competence develops, rather than removed abruptly.',
        ],
      },
      {
        heading: 'Research on Problem-Solving Outcomes',
        content: [
          'Raslan\'s 2024 study specifically examined how ZPD-based scaffolding affects problem-solving skills. The results showed significant improvements in students\' ability to tackle complex, multi-step problems when they received appropriately scaffolded instruction.',
          'Importantly, students who received scaffolded support developed better metacognitive skills — they became more aware of their own thinking processes and better at self-monitoring their problem-solving approaches.',
        ],
      },
      {
        heading: 'AI and Automated Scaffolding',
        content: [
          'Modern adaptive learning systems can implement scaffolding principles at scale through intelligent hint systems, adaptive difficulty adjustment, and personalised worked examples. The challenge is calibrating support correctly — too little help leads to frustration, while too much prevents genuine learning.',
          'AI systems can analyse patterns in student responses to detect when learners are struggling within their ZPD versus when they\'re facing tasks outside their current capability. This enables precise calibration of support level.',
        ],
      },
    ],
    keyTakeaways: [
      'The ZPD is the space between independent capability and current potential with support — the optimal learning zone',
      'Scaffolding should be contingent (responsive to specific difficulties) and faded (gradually reduced)',
      'ZPD-based instruction particularly improves problem-solving and metacognitive skills',
      'Effective scaffolding requires continuous assessment of individual student capability',
      'AI systems can implement personalised scaffolding by detecting optimal challenge levels',
    ],
    implications: 'For learning platforms, ZPD theory provides a framework for difficulty calibration. Rather than simply presenting easier or harder content, effective systems provide graduated support within challenging tasks. This keeps students in their ZPD — challenged enough to grow but supported enough to succeed. Lumen8\'s adaptive engine uses these principles to ensure every student is working at their optimal challenge level.',
  },
};

const BlogPost: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const post = slug ? blogPostContent[slug] : null;

  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  // Find next and previous posts
  const currentIndex = blogPosts.findIndex(p => p.slug === slug);
  const prevPost = currentIndex > 0 ? blogPosts[currentIndex - 1] : null;
  const nextPost = currentIndex < blogPosts.length - 1 ? blogPosts[currentIndex + 1] : null;

  return (
    <>
      {/* Hero Section */}
      <section className="relative pt-20 pb-12 bg-gradient-to-b from-[var(--color-midnight)] to-[var(--color-dark-navy)] overflow-hidden">
        <div className="absolute inset-0">
          <div className={`absolute top-20 right-10 w-72 h-72 bg-gradient-to-br ${post.gradient} rounded-full filter blur-[128px] opacity-20`}></div>
        </div>

        <Container size="md" className="relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Link to="/blog" className="inline-flex items-center gap-2 text-[var(--color-medium-gray)] hover:text-[var(--color-electric-blue)] transition-colors mb-8">
              <ArrowLeftIcon className="w-4 h-4" />
              Back to Blog
            </Link>

            <div className="flex items-center gap-4 mb-6">
              <span className="px-3 py-1 rounded-full text-xs font-semibold bg-[var(--color-electric-blue)]/20 text-[var(--color-electric-blue)]">
                {post.category}
              </span>
              <div className="flex items-center gap-4 text-sm text-[var(--color-medium-gray)]">
                <div className="flex items-center gap-2">
                  <CalendarIcon className="w-4 h-4" />
                  <span>{post.date}</span>
                </div>
                <div className="flex items-center gap-2">
                  <ClockIcon className="w-4 h-4" />
                  <span>{post.readTime}</span>
                </div>
              </div>
            </div>

            <h1 className="text-4xl md:text-5xl font-black text-white mb-6 leading-tight">
              {post.title}
            </h1>
          </motion.div>
        </Container>
      </section>

      {/* Article Content */}
      <section className="py-12 bg-[var(--color-dark-navy)]">
        <Container size="md">
          <motion.article
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="prose prose-lg prose-invert max-w-none"
          >
            {post.sections.map((section, index) => (
              <div key={index} className="mb-8">
                {section.heading && (
                  <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
                    {section.heading}
                  </h2>
                )}
                {section.content.map((paragraph, pIndex) => (
                  <p key={pIndex} className="text-[var(--color-light-gray)] leading-relaxed mb-4">
                    {paragraph}
                  </p>
                ))}
              </div>
            ))}

            {/* Key Takeaways */}
            <div className="glass-card p-8 my-12">
              <h3 className="text-xl font-bold text-white mb-4">Key Takeaways</h3>
              <ul className="space-y-3">
                {post.keyTakeaways.map((takeaway, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-[var(--color-electric-blue)]/20 flex items-center justify-center mt-0.5">
                      <span className="text-xs font-bold text-[var(--color-electric-blue)]">{index + 1}</span>
                    </div>
                    <span className="text-[var(--color-light-gray)]">{takeaway}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Implications Section */}
            <div className="card-premium p-8 my-12">
              <h3 className="text-xl font-bold text-white mb-4">Implications for Education</h3>
              <p className="text-[var(--color-light-gray)] leading-relaxed">
                {post.implications}
              </p>
            </div>

            {/* Citation */}
            <div className="border-l-4 border-[var(--color-electric-blue)] pl-6 my-12">
              <p className="text-sm text-[var(--color-medium-gray)] italic">
                <strong className="text-[var(--color-light-gray)]">Research Citation:</strong><br />
                {post.citation}
              </p>
            </div>
          </motion.article>
        </Container>
      </section>

      {/* Navigation */}
      <section className="py-12 bg-gradient-to-b from-[var(--color-dark-navy)] to-[var(--color-midnight)]">
        <Container size="md">
          <div className="grid md:grid-cols-2 gap-6">
            {prevPost && (
              <Link to={`/blog/${prevPost.slug}`} className="group">
                <div className="card-premium p-6 h-full hover-glow transition-all">
                  <div className="flex items-center gap-2 text-sm text-[var(--color-medium-gray)] mb-2">
                    <ArrowLeftIcon className="w-4 h-4" />
                    Previous Article
                  </div>
                  <h4 className="text-lg font-semibold text-white group-hover:text-[var(--color-electric-blue)] transition-colors">
                    {prevPost.title}
                  </h4>
                </div>
              </Link>
            )}
            {nextPost && (
              <Link to={`/blog/${nextPost.slug}`} className="group md:ml-auto">
                <div className="card-premium p-6 h-full hover-glow transition-all">
                  <div className="flex items-center justify-end gap-2 text-sm text-[var(--color-medium-gray)] mb-2">
                    Next Article
                    <ArrowRightIcon className="w-4 h-4" />
                  </div>
                  <h4 className="text-lg font-semibold text-white group-hover:text-[var(--color-electric-blue)] transition-colors text-right">
                    {nextPost.title}
                  </h4>
                </div>
              </Link>
            )}
          </div>
        </Container>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-[var(--color-midnight)]">
        <Container>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center max-w-2xl mx-auto"
          >
            <BookOpenIcon className="w-12 h-12 text-[var(--color-electric-blue)] mx-auto mb-6" />
            <h2 className="text-3xl font-bold text-white mb-4">
              Experience Evidence-Based Learning
            </h2>
            <p className="text-[var(--color-light-gray)] mb-8">
              Lumen8 puts these research principles into practice. See how adaptive learning transforms educational outcomes.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" className="btn-premium px-6 py-3 inline-flex items-center gap-2">
                Request a Demo
                <ArrowRightIcon className="w-4 h-4" />
              </Link>
              <Link to="/blog" className="btn-glass px-6 py-3">
                Read More Articles
              </Link>
            </div>
          </motion.div>
        </Container>
      </section>
    </>
  );
};

export default BlogPost;
